import { prepareAssertions } from '~/packages/general/variable/prepareAssertions.util'
import { Variable } from '~/packages/general/variable/variables.typing'
import { defineAction, enumerated } from '~/prix'
import { analysis } from '~/prix/types/presets/analysis'
import { assertions } from '~/prix/types/presets/assertions'
import { spaceForPoint } from '~/prix/types/presets/space'
import { timeForIncidence } from '~/prix/types/presets/time'
import { variable } from '~/prix/types/presets/variable'
import bmiStatsAction from './bmiStats.action.json' with { type: 'json' }
import { z } from 'zod'
import expression from '~/prix/types/expression'
import { getZodSchemaInfo } from '~/prix/types/zodType'
import { select } from '~/prix/types/presets/select'
import { attendanceShape } from '../../attendance/stats/attendanceStats.definition'
import { healthVariableBaseRelevance } from '~/packages/general/variable/healthVariableRelevance.util'
import { defaultDetectionLayers } from '~/utils/server/stats/defaultTimeSeriesLayers'

export const bmiShape = z.object({
  classification: enumerated({
    'Abaixo do peso': 'Abaixo do peso',
    'Peso normal': 'Peso normal',
    Sobrepeso: 'Sobrepeso',
    Obesidade: 'Obesidade',
  }).describe('Classificação do IMC'),
  ...attendanceShape.shape,
})

const expressionForCharacteristics = expression(bmiShape.shape)
const expressionForAssertions = expression(
  bmiShape.pick({
    'citizen.sex': true,
  }).shape,
)

export const bmiVariable: Variable = {
  key: 'attendanceBmi',
  actionDefinitionKey: bmiStatsAction.key,
  title: 'Registro de IMC',
  categoryKey: 'healthAttendances',
  unitSingular: 'registro',
  unitPlural: 'registros',
  grammaticalGender: 'male',
  timeType: 'incidence',
  shape: getZodSchemaInfo(bmiShape),
  betterDensity: 'less',
  relevance: healthVariableBaseRelevance.bmi,
  basicSelect: ['citizen.name', 'citizen.socialName'],
  detailedSelect: [
    'classification',

    /* CIDADÂO */
    'citizen.phoneNumber',
    'citizen.cns',
    'citizen.cpf',
    'citizen.sex',
    'citizen.birthDate',
    'citizen.latitude',
    'citizen.longitude',
    'citizen.deceased',
    'citizen.originalId',

    /* ATENDIMENTO */
    'attendanceDateTime',
    'professionalName',
    'healthUnitName',
    'weight',
    'height',
    'icd10Description',
    'icpc2Description',
    'teamName',
    'typeOfAttendance',
    'isVaccinationUpToDate',
    'lastMenstruationDate',
    'isObservation',
    'isScheduleReturn',
    'isScheduleForGroups',
    'isScheduleForNasf',
    'isScheduleForContinuedCare',
    'isEpisodeDischarge',
    'hasReferralToSpecializedService',
    'hasReferralToCaps',
    'hasReferralToHospitalization',
    'hasReferralToUrgency',
    'hasIntersectoralReferral',
    'hasReferralToHomeCareService',

    /* SAÚDE */
    'citizenHealth.email',
    'citizenHealth.isResponsibleFamilyMember',
    'citizenHealth.hasDiabetes',
    'citizenHealth.hasHypertension',
    'citizenHealth.hasMedicalProfessionalDiagnosisOfMentalConditions',
    'citizenHealth.isPregnant',
    'citizenHealth.smoker',
    'citizenHealth.skinColorOrRace',
    'citizenHealth.educationLevel',
    'citizenHealth.employmentStatus',
    'citizenHealth.homeless',
    'citizenHealth.hasPrivateHealthInsurance',
    'citizenHealth.nationality',

    /* LOCALIZAÇÃO */
    'citizen.latitude',
    'citizen.longitude',
    'citizen.streetName',
    'citizen.streetNumber',
    'citizen.neighborhood',
    'citizen.postalCode',
    'citizen.referencePoint',
    'citizen.complement',
  ],
  filterableBy: getZodSchemaInfo(
    bmiShape.pick({
      classification: true,
      'citizen.sex': true,
      'citizen.age': true,
      'citizen.deceased': true,

      'citizenHealth.isResponsibleFamilyMember': true,
      'citizenHealth.hasDiabetes': true,
      'citizenHealth.hasHypertension': true,
      'citizenHealth.hadStroke': true,
      'citizenHealth.hadHeartAttack': true,
      'citizenHealth.hasHeartDisease': true,
      'citizenHealth.hasRespiratoryDisease': true,
      'citizenHealth.hadKidneyDisease': true,
      'citizenHealth.hasLeprosy': true,
      'citizenHealth.hasTuberculosis': true,
      'citizenHealth.hadCancer': true,
      'citizenHealth.hasMedicalProfessionalDiagnosisOfMentalConditions': true,
      'citizenHealth.hasAnyDisability': true,
      'citizenHealth.isPregnant': true,
      'citizenHealth.isBedridden': true,
      'citizenHealth.isDomiciled': true,

      'citizenHealth.smoker': true,
      'citizenHealth.consumesAlcoholicBeverages': true,
      'citizenHealth.takeDrugs': true,
      'citizenHealth.currentWeightStatus': true,
      'citizenHealth.eatHowManyTimesDay': true,

      'citizenHealth.skinColorOrRace': true,
      'citizenHealth.nationality': true,
      'citizenHealth.educationLevel': true,
      'citizenHealth.employmentStatus': true,
      'citizenHealth.sexualOrientation': true,
      'citizenHealth.kinshipWithFamilyResponsible': true,

      'citizenHealth.unknownMother': true,
      'citizenHealth.unknownFather': true,
      'citizenHealth.attendsSchoolOrDaycare': true,

      'citizenHealth.memberOfTraditionalGroupsOrCommunity': true,
      'citizenHealth.takePartInCommunityGroups': true,

      'citizenHealth.homeless': true,
      'citizenHealth.homelessForHowManyTime': true,
      'citizenHealth.receivesBenefit': true,
      'citizenHealth.hasPrivateHealthInsurance': true,
    }),
  ),
  timeSeries: {
    model: 'additive',
    fitDays: 365 * 10,
    layers: [
      ...defaultDetectionLayers,
      {
        layer: 'calendarSeasonality',
        type: 'monthOfYear',
        degree: 1,
        optimization: {
          minimumDays: 1095,
          lossFunction: 'recentWeightedMeanSquaredError',
        },
      },
      {
        layer: 'trend',
        degree: 2,
      },
      {
        layer: 'calendarSeasonality',
        type: 'dayOfWeek',
        degree: 1,
        optimization: {
          minimumDays: 60,
          lossFunction: 'recentWeightedMeanSquaredError',
        },
      },
      {
        layer: 'calendarSeasonality',
        type: 'holiday',
        degree: 1,
      },
      {
        layer: 'calendarSeasonality',
        type: 'dayOfMonth',
        degree: 1,
      },
      {
        layer: 'harmonicSeasonality',
        optimization: {
          minimumDays: 30,
          lossFunction: 'gradientMeanSquaredError',
        },
      },
      {
        layer: 'harmonicSeasonality',
        optimization: {
          minimumDays: 30,
          lossFunction: 'gradientMeanSquaredError',
        },
      },
    ],
  },
  assertions: prepareAssertions(expressionForAssertions),
}

export const bmiAverageVariable: Variable = {
  ...bmiVariable,
  key: 'attendanceBmiAverage',
  title: 'Valor médio de IMC',
  unitSingular: 'kg/m²',
  unitPlural: 'kg/m²',
  betterDensity: 'less',
  timeSeries: {
    ...bmiVariable.timeSeries,
    aggregationMode: 'mean',
    ignoreZeroes: true,
  },
  customValue: {
    type: 'expression',
    kind: 'function',
    name: 'avg',
    arguments: [
      {
        type: 'expression',
        kind: 'property',
        property: 'bmi',
      },
    ],
  },
}

const bmiStatsDefinition = defineAction({
  ...bmiStatsAction,
  input: z.object({
    variable,
    highlight: expressionForCharacteristics.optional(),
    filter: expressionForCharacteristics.optional(),
    space: spaceForPoint().optional(),
    time: timeForIncidence().optional(),
    select: select(bmiShape),
    assertions,
    analysis,
  }),
  output: z.any(),
  cache: input => {
    if (
      input.filter ||
      input.space?.delimitationType === 'custom' ||
      (input.time?.type &&
        ['customDateRange', 'customDateRangePredicted'].includes(input.time?.type))
    ) {
      return {}
    }
    return {
      back: {
        expirationMsForFallback: 1000 * 60 * 60 * 24 * 7,
        expirationMsForUpdate: 1000 * 60 * 60 * 2,
      },
      front: {
        expirationMsForFallback: 1000 * 60 * 60 * 24 * 7,
        expirationMsForUpdate: 1000 * 60 * 60 * 2,
      },
    }
  },
})

export default bmiStatsDefinition
